import { useContext, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, matchPath } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import { routePermissions } from "./systemPermissions";
import Cookies from "universal-cookie";
import { useQuery } from "@tanstack/react-query";
import { retrieveUserProfileService } from "../utils/api/usermanagement/profile/retrieveUser";
import { retrieveRoleByIdService } from "../utils/outbound/role/retrieveRoleById";
import { retrieveRoleService } from "../utils/api/usermanagement/role/retrieveRole";
import { retrieveAccessControlByIdService } from "../utils/api/usermanagement/accesscontrol/retrieveAccessControlById";
import { AccessControlType } from "../types/app.type";

export const RouteProtector: React.FC = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const initialAccessControl = useMemo(
		() => [
			{ module: "Accounting", rights: [] },
			{ module: "Approval", rights: [] },
			{ module: "Configuration", rights: [] },
			{ module: "Customer Service", rights: [] },
			{ module: "Dashboard", rights: [] },
			{ module: "FTD Management", rights: [] },
			{ module: "Loan Management", rights: [] },
			{ module: "Report", rights: [] },
			{ module: "Teller Management", rights: [] },
			{ module: "Transactions", rights: [] },
			{ module: "User Management", rights: [] },
		],
		[]
	);
	const location = useLocation();
	const { isAuthenticated, loading } = useContext(AuthContext);

	const [userRole, setUserRole] = useState({
		title: "",
		accessControl: "",
	});
	const [accessControl, setAccessControl] =
		useState<AccessControlType[]>(initialAccessControl);

	const { data: loggedInUser } = useQuery({
		queryKey: ["loggedInUser", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN && !loading,
	});

	useEffect(() => {
		const fetchUserRole = async () => {
			if (!loggedInUser || loading) return;
			const roleId = loggedInUser.roles[loggedInUser.roles.length - 1];
			try {
				const retrievedRole = await retrieveRoleByIdService(
					loggedInUser._id,
					roleId
				);
				setUserRole({
					title: retrievedRole?.title,
					accessControl: retrievedRole?.accessControl,
				});
			} catch (error) {
				try {
					const retrievedRole = await retrieveRoleService(TOKEN, roleId);
					setUserRole({
						title: retrievedRole?.title,
						accessControl: retrievedRole?.accessControl,
					});
				} catch (error) {
					console.error("Role not found:", error);
				}
			}
		};
		fetchUserRole();
	}, [loggedInUser, TOKEN, loading]);

	useEffect(() => {
		const fetchAccessControl = async (id: string) => {
			if (!id.trim()) {
				return setAccessControl(initialAccessControl);
			}
			try {
				const data = await retrieveAccessControlByIdService(TOKEN, id);
				// Transform the data to extract only the title values
				const transformedPermissions = data.permissions.map(
					(permission: Record<string, any>) => ({
						module: permission.module,
						rights: permission.rights.map(
							(right: Record<string, any>) => right.title
						),
					})
				);
				setAccessControl(
					transformedPermissions.length > 0
						? transformedPermissions
						: initialAccessControl
				);
			} catch (err) {
				console.error("Failed to fetch access control:", err);
			}
		};
		fetchAccessControl(userRole?.accessControl);
	}, [TOKEN, initialAccessControl, userRole]);

	if (loading) return null;

	const matchedPermission = routePermissions.find((permission) =>
		matchPath({ path: permission.url, end: true }, location.pathname)
	);

	if (matchedPermission && userRole?.title !== "MFI Admin") {
		const moduleAccess = accessControl.find(
			(item) => item.module === matchedPermission.module
		);
		const hasPermission = moduleAccess?.rights?.includes(
			matchedPermission.title
		);
		if (!hasPermission) {
			return <Navigate to="/unauthorized" replace />;
		}
	}
	return isAuthenticated ? <Outlet /> : <Navigate to="/" replace />;
};
